import { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Receipt, Add, Trash } from "iconsax-react";
import { ModalForm, Input, DatePicker, Button, ToggleButton, Toast, Prompt } from "@/shared/ui";
import { extractErrorMessage, formatMoneyKzt, type Locale } from "@/shared/utils";
import { useSettleAdvanceMutation } from "../../../api";
import { settleAdvanceSchema, extractVat, type SettleAdvanceFormValues } from "../../../utils/settlementValidation";

interface Props {
  advanceId: number;
  advanceAmount: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface PromptState {
  title: string;
  text: string;
  variant?: "success" | "error" | "warning";
}

export default function AdvanceSettlementForm({ advanceId, advanceAmount, onClose, onSuccess }: Props) {
  const { t, i18n } = useTranslation("AdvancesPage");
  const locale = (i18n.language as Locale) || "ru";
  const [prompt, setPrompt] = useState<PromptState | null>(null);
  const [settleAdvance, { isLoading }] = useSettleAdvanceMutation();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SettleAdvanceFormValues>({
    resolver: zodResolver(settleAdvanceSchema),
    defaultValues: {
      lines: [{ description: "", document_no: "", document_date: null, gross_amount: "", has_esf: false }],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "lines" });
  const lines = watch("lines");

  const spentGross = (lines || []).reduce((sum, line) => sum + (Number(line.gross_amount) || 0), 0);
  const vatTotal = (lines || []).reduce(
    (sum, line) => sum + (line.has_esf ? extractVat(Number(line.gross_amount) || 0) : 0),
    0,
  );
  const netTotal = spentGross - vatTotal;
  const advanceNum = Number(advanceAmount) || 0;
  const diff = advanceNum - spentGross;

  async function onSubmit(data: SettleAdvanceFormValues) {
    if (isLoading) return;
    try {
      await settleAdvance({
        id: advanceId,
        lines: data.lines.map(function buildLine(line) {
          return {
            description: line.description || undefined,
            document_no: line.document_no || undefined,
            document_date: line.document_date ? line.document_date.toISOString().split("T")[0] : null,
            gross_amount: line.gross_amount,
            has_esf: line.has_esf,
          };
        }),
      }).unwrap();
      setPrompt({
        title: t("settlement.messages.successTitle"),
        text: t("settlement.messages.successText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("settlement.messages.errorTitle"),
        text: extractErrorMessage(err, "settlement.messages.errorText", t),
        variant: "error",
      });
    }
  }

  function handlePromptClose() {
    const wasSuccess = prompt?.variant === "success";
    setPrompt(null);
    if (wasSuccess) onSuccess();
  }

  return (
    <>
      <ModalForm
        icon={Receipt}
        onClose={onClose}
        resize={false}
        footer={
          <div className="grid grid-cols-[2fr_3fr] gap-3">
            <Button variant="secondary" size="huge" onClick={onClose} type="button">
              {t("settlement.buttons.cancel")}
            </Button>
            <Button variant="primary" size="huge" type="submit" form="settle-advance-form" disabled={isLoading}>
              {isLoading ? t("settlement.buttons.loading") : t("settlement.buttons.submit")}
            </Button>
          </div>
        }>
        <form id="settle-advance-form" className="flex flex-col h-full" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-2 shrink-0 pb-5 border-b surface-base-stroke">
            <h4 className="text-display-2xs content-base-primary">{t("settlement.advanceTitle")}</h4>
            <p className="text-body-regular-sm content-base-secondary">{t("settlement.advanceSubtitle")}</p>
          </div>

          <div className="flex-1 overflow-auto flex flex-col gap-4 p-1 my-5 page-scroll">
            {fields.map(function renderLine(field, index) {
              return (
                <div key={field.id} className="flex flex-col gap-3 p-4 radius-md surface-secondary-fill">
                  <div className="flex items-center justify-between">
                    <p className="text-body-bold-md content-base-primary">
                      {t("settlement.line")} {index + 1}
                    </p>
                    <Button variant="danger" size="sm" type="button" onClick={() => remove(index)}>
                      <Trash size={16} color="currentColor" />
                      {t("settlement.buttons.removeLine")}
                    </Button>
                  </div>
                  <Input
                    label={t("settlement.fields.description")}
                    placeholder={t("settlement.placeholders.description")}
                    {...register(`lines.${index}.description`)}
                  />
                  <Input
                    label={t("settlement.fields.documentNo")}
                    placeholder={t("settlement.placeholders.documentNo")}
                    {...register(`lines.${index}.document_no`)}
                  />
                  <DatePicker
                    locale={locale}
                    mode="single"
                    label={t("settlement.fields.documentDate")}
                    value={watch(`lines.${index}.document_date`) ?? null}
                    onChange={(date) => setValue(`lines.${index}.document_date`, date, { shouldValidate: true })}
                  />
                  <Input
                    label={t("settlement.fields.grossAmount")}
                    placeholder="0"
                    {...register(`lines.${index}.gross_amount`)}
                    error={
                      errors.lines?.[index]?.gross_amount?.message
                        ? t(errors.lines[index]!.gross_amount!.message as string)
                        : undefined
                    }
                  />
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-body-bold-sm content-base-primary">{t("settlement.fields.hasEsf")}</span>
                      <span className="text-body-regular-sm content-base-secondary">{t("settlement.fields.hasEsfHint")}</span>
                    </div>
                    <ToggleButton
                      checked={watch(`lines.${index}.has_esf`) ?? false}
                      onChange={(checked) => setValue(`lines.${index}.has_esf`, checked, { shouldValidate: true })}
                    />
                  </div>
                </div>
              );
            })}

            <Button
              variant="tertiary"
              size="md"
              type="button"
              onClick={() => append({ description: "", document_no: "", document_date: null, gross_amount: "", has_esf: false })}>
              <Add size={16} color="currentColor" />
              {t("settlement.buttons.addLine")}
            </Button>

            <div className="flex flex-col gap-2 p-4 radius-md surface-secondary-fill">
              <div className="flex items-center justify-between">
                <span className="text-body-regular-sm content-base-secondary">{t("settlement.summary.advanceAmount")}</span>
                <span className="text-body-bold-sm content-base-primary">{formatMoneyKzt(String(advanceNum))}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-body-regular-sm content-base-secondary">{t("settlement.summary.spent")}</span>
                <span className="text-body-bold-sm content-base-primary">{formatMoneyKzt(String(spentGross))}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-body-regular-sm content-base-secondary">{t("settlement.summary.vat")}</span>
                <span className="text-body-bold-sm content-base-primary">{formatMoneyKzt(String(vatTotal))}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-body-regular-sm content-base-secondary">{t("settlement.summary.net")}</span>
                <span className="text-body-bold-sm content-base-primary">{formatMoneyKzt(String(netTotal))}</span>
              </div>
            </div>

            {diff > 0 && (
              <Toast
                color="grey"
                text={t("settlement.summary.refund", { amount: formatMoneyKzt(String(diff)) })}
                closable={false}
                autoClose={false}
                isFullWidth
              />
            )}
            {diff < 0 && (
              <Toast
                color="notice"
                text={t("settlement.summary.overspend", { amount: formatMoneyKzt(String(Math.abs(diff))) })}
                closable={false}
                autoClose={false}
                isFullWidth
              />
            )}
          </div>
        </form>
      </ModalForm>

      {isLoading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("settlement.messages.loading")}
          additionalText={t("messages.pleaseWait")}
          namespace="AdvancesPage"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "success"}
          onClose={handlePromptClose}
          namespace="AdvancesPage"
        />
      )}
    </>
  );
}
